import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Filter, Activity, ClipboardList, ChevronRight, MoreHorizontal, Phone, CalendarCheck, CheckSquare, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useAuth } from '@core/auth/useAuth';
import { usePermissions, canViewAssigneeActivity } from '@core/auth/usePermissions';
import { applicationService } from '@core/supabase/services/applicationService';
import { activityService } from '@core/supabase/services/activityService';
import { clientService } from '@core/supabase/services/clientService';
import { Application, ApplicationStatus, Client } from '@core/types';
import { PageHeader } from '@shared/components/ui/PageHeader';
import { useToast } from '@shared/hooks/useToast';
import { formatCreatedWhen } from '@shared/lib/formatCreatedWhen';
import { formatDateTimeGmt } from '@shared/lib/formatDateTimeGmt';
import { EmptyState } from '@shared/components/ui/EmptyState';
import { FullPageSpinner } from '@shared/components/ui/Spinner';
import { StatusBadge } from '@shared/components/ui/StatusBadge';
import { TablePagination } from '@shared/components/ui/TablePagination';
import { DataTableToolbar } from '@shared/components/ui/DataTableToolbar';
import { ListConfigureButton } from '@shared/components/ui/ListConfigureButton';
import { ListOperationsDropdown, type ListExportFmt } from '@shared/components/ui/ListOperationsDropdown';
import { objectHistoryUrl } from '@shared/lib/objectHistoryUrl';
import { ConfigureListColumnsModal, colClass } from '@shared/components/ui/ConfigureListColumnsModal';
import { useListColumnVisibility } from '@shared/hooks/useListColumnVisibility';
import type { ListColumnCatalogItem } from '@shared/lib/listColumnPreferences';
import { listExportNotImplemented } from '@shared/lib/listExportStub';
import { SortableTh } from '@shared/components/ui/SortableTableHeader';
import { TableAvatar } from '@shared/components/ui/TableAvatar';
import { GenericManageRightsModal } from '@shared/components/list/GenericManageRightsModal';

const STATUS_LABEL: Record<ApplicationStatus, string> = {
  submitted: 'Submitted', viewed_by_client: 'Viewed', shortlisted: 'Shortlisted',
  interview_1_scheduled: 'Interview 1 Sched.', interview_1_conducted: 'Interview 1 Done',
  interview_2_scheduled: 'Interview 2 Sched.', interview_2_conducted: 'Interview 2 Done',
  offer_extended: 'Offer Extended', offer_accepted: 'Accepted', offer_rejected: 'Rejected',
  placed: 'Placed', rejected: 'Rejected', no_show: 'No Show', on_hold: 'On Hold', closed: 'Closed',
};

function formatTimeAgo(dateStr: string) {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMs / 3600000);
  const days = Math.floor(diffMs / 86400000);
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return formatDateTimeGmt(dateStr);
}

const ACTION_LABEL: Record<string, string> = {
  created: 'created',
  updated: 'updated',
  status_changed: 'changed status',
  assigned: 'assigned',
  email_sent: 'sent email',
  cta_responded: 'client responded',
};

const FEED_ENTITY_VALUES = new Set([
  'application',
  'candidate',
  'job',
  'client',
  'requisition',
  'email',
]);
const FEED_ACTION_VALUES = new Set(Object.keys(ACTION_LABEL));

const ACTIVITIES_ASSIGNED_COLUMNS: ListColumnCatalogItem[] = [
  { id: 'candidate', label: 'Candidate' },
  { id: 'job', label: 'Job' },
  { id: 'client', label: 'Client' },
  { id: 'status', label: 'Status' },
  { id: 'created_when', label: 'Created when' },
  { id: 'updated', label: 'Updated' },
];

type ActivityTab = 'assigned' | 'feed';

type DateRangeFilter = 'all' | 'today' | 'week' | 'month';

function parseDateRange(s: string | null): DateRangeFilter {
  if (s === 'today' || s === 'week' || s === 'month') return s;
  return 'all';
}

function FeedPayloadLinks({ payload }: { payload: Record<string, unknown> | null }) {
  const p = payload ?? {};
  const pairs: { to: string; label: string }[] = [];
  const appId = typeof p.application_id === 'string' ? p.application_id : undefined;
  const candId = typeof p.candidate_id === 'string' ? p.candidate_id : undefined;
  const jobId = typeof p.job_id === 'string' ? p.job_id : undefined;
  const clientId = typeof p.client_id === 'string' ? p.client_id : undefined;
  if (appId) pairs.push({ to: `/applications/${appId}`, label: 'application' });
  if (candId) pairs.push({ to: `/candidates/${candId}`, label: 'candidate' });
  if (jobId) pairs.push({ to: `/jobs/${jobId}`, label: 'job' });
  if (clientId) pairs.push({ to: `/clients/${clientId}`, label: 'client' });
  if (pairs.length === 0) return null;
  const nodes: ReactNode[] = [];
  pairs.forEach((l, i) => {
    if (i > 0) nodes.push(<span key={`sep-${i}`} className="vscode-text-tertiary"> · </span>);
    nodes.push(
      <Link key={l.to} to={l.to} className="text-brand-400 hover:text-brand-300">
        {l.label}
      </Link>
    );
  });
  return <span className="ml-1 inline-flex flex-wrap items-baseline gap-x-0">{nodes}</span>;
}

function safeDecodeURIComponent(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

export function ActivitiesPage() {
  const { user } = useAuth();
  const perms = usePermissions();
  const { toast, showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: ActivityTab = searchParams.get('tab') === 'feed' ? 'feed' : 'assigned';
  const dateFilter = parseDateRange(searchParams.get('range'));
  const qFromUrl = searchParams.get('q') ?? '';
  const actorIdParam = searchParams.get('actorId')?.trim() || '';
  const actorDisplayName = searchParams.get('actorName')
    ? safeDecodeURIComponent(searchParams.get('actorName')!)
    : null;
  const canSeeActorFeed =
    !actorIdParam || canViewAssigneeActivity(perms, user?.orgUserId, actorIdParam);
  const entParam = searchParams.get('entity');
  const feedEntityFilter = entParam && FEED_ENTITY_VALUES.has(entParam) ? entParam : '';
  const actParam = searchParams.get('action');
  const feedActionFilter = actParam && FEED_ACTION_VALUES.has(actParam) ? actParam : '';
  const stParam = searchParams.get('status');
  const statusFilter = (stParam && stParam in STATUS_LABEL ? stParam : '') as ApplicationStatus | '';
  const clientFilter = searchParams.get('client') ?? '';

  const patchParams = (updates: Record<string, string | undefined>) => {
    setSearchParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(updates)) {
          if (v === undefined || v === '') n.delete(k);
          else n.set(k, v);
        }
        return n;
      },
      { replace: true }
    );
  };

  const [applications, setApplications] = useState<Application[]>([]);
  const [activities, setActivities] = useState<Array<{
    id: string;
    entity: string;
    action: string;
    payload: Record<string, unknown> | null;
    created_at: string;
    actor: { display_name: string } | null;
  }>>([]);
  const [clients, setClients] = useState<{ id: string; name: string }[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const headerCheckRef = useRef<HTMLInputElement>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [searchInput, setSearchInput] = useState(qFromUrl);
  const [configureOpen, setConfigureOpen] = useState(false);
  const [manageRightsOpen, setManageRightsOpen] = useState(false);
  const navigate = useNavigate();

  const actCatalog = useMemo(() => ACTIVITIES_ASSIGNED_COLUMNS, []);
  const { show, setColumn, reset, visible } = useListColumnVisibility(
    'activities_assigned',
    user?.orgId ?? null,
    actCatalog,
  );

  useEffect(() => {
    setSearchInput(qFromUrl);
  }, [qFromUrl]);

  const queueSearchParam = (v: string) => {
    setSearchInput(v);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      searchDebounceRef.current = null;
      const trimmed = v.trim();
      setSearchParams(
        (prev) => {
          const n = new URLSearchParams(prev);
          if (trimmed) n.set('q', trimmed);
          else n.delete('q');
          return n;
        },
        { replace: true }
      );
    }, 400);
  };

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (!user?.orgId) return;
    if (actorIdParam && !canSeeActorFeed) {
      setApplications([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const filters: Parameters<typeof applicationService.list>[1] = {};
    const assignee = actorIdParam || user.orgUserId;
    if (assignee) filters.assignedTo = assignee;
    if (statusFilter) filters.status = statusFilter;
    if (clientFilter) filters.clientId = clientFilter;
    applicationService.list(user.orgId, filters).then(setApplications).finally(() => setLoading(false));
  }, [user?.orgId, user?.orgUserId, statusFilter, clientFilter, actorIdParam, canSeeActorFeed]);

  useEffect(() => {
    if (!user?.orgId || tab !== 'feed') return;
    if (actorIdParam && !canSeeActorFeed) {
      setActivities([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const since =
      dateFilter === 'today' ? today.toISOString() :
      dateFilter === 'week'  ? new Date(today.getTime() - 7 * 86400000).toISOString() :
      dateFilter === 'month' ? new Date(today.getTime() - 30 * 86400000).toISOString() :
      undefined;
    activityService.listByOrg(user.orgId, {
      limit: 100,
      entity: (feedEntityFilter || undefined) as Parameters<typeof activityService.listByOrg>[1]['entity'],
      action: (feedActionFilter || undefined) as Parameters<typeof activityService.listByOrg>[1]['action'],
      since,
      actorId: actorIdParam || undefined,
    })
      .then(setActivities as (a: unknown) => void)
      .finally(() => setLoading(false));
  }, [user?.orgId, tab, feedEntityFilter, feedActionFilter, dateFilter, actorIdParam, canSeeActorFeed]);

  useEffect(() => {
    if (!user?.orgId) return;
    clientService.list(user.orgId).then((c) => {
      const list = Array.isArray(c) ? c : (c as { data: Client[] }).data;
      setClients(list.map((x) => ({ id: x.id, name: x.name })));
    });
  }, [user?.orgId]);

  useEffect(() => {
    setPage(1);
  }, [tab, statusFilter, clientFilter, dateFilter, feedEntityFilter, feedActionFilter, qFromUrl, actorIdParam]);

  const filteredAssigned = useMemo(() => {
    let list = applications;
    if (searchInput) {
      const q = searchInput.toLowerCase();
      list = list.filter(
        (a) =>
          (a.candidate?.full_name ?? '').toLowerCase().includes(q) ||
          (a.job?.title ?? '').toLowerCase().includes(q) ||
          (a.client?.name ?? '').toLowerCase().includes(q),
      );
    }
    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      list = list.filter((a) => {
        const d = new Date(a.status_updated_at);
        if (dateFilter === 'today') return d >= today;
        if (dateFilter === 'week') return d >= new Date(today.getTime() - 7 * 86400000);
        if (dateFilter === 'month') return d >= new Date(today.getTime() - 30 * 86400000);
        return true;
      });
    }
    return list;
  }, [applications, searchInput, dateFilter]);

  type ActSortCol = 'candidate' | 'job' | 'client' | 'status' | 'created_when' | 'updated';
  const [actSortBy, setActSortBy] = useState<ActSortCol>('created_when');
  const [actSortAsc, setActSortAsc] = useState(false);

  const sortedAssigned = useMemo(() => {
    const arr = [...filteredAssigned];
    const dir = actSortAsc ? 1 : -1;
    const str = (a: Application, b: Application, get: (x: Application) => string) =>
      dir * get(a).localeCompare(get(b), undefined, { sensitivity: 'base' });
    arr.sort((a, b) => {
      switch (actSortBy) {
        case 'candidate':
          return str(a, b, (x) => x.candidate?.full_name ?? '');
        case 'job':
          return str(a, b, (x) => x.job?.title ?? '');
        case 'client':
          return str(a, b, (x) => x.client?.name ?? '');
        case 'status':
          return str(a, b, (x) => x.status);
        case 'updated':
          return dir * (new Date(a.status_updated_at).getTime() - new Date(b.status_updated_at).getTime());
        case 'created_when':
        default:
          return dir * (new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      }
    });
    return arr;
  }, [filteredAssigned, actSortBy, actSortAsc]);

  const handleActSort = (col: ActSortCol) => {
    if (actSortBy === col) {
      setActSortAsc((x) => !x);
    } else {
      setActSortBy(col);
      setActSortAsc(col === 'created_when' ? false : true);
    }
    setPage(1);
  };

  const totalItems = tab === 'assigned' ? sortedAssigned.length : activities.length;
  const paginatedAssigned = sortedAssigned.slice((page - 1) * pageSize, page * pageSize);
  const paginatedFeed = activities.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    if (headerCheckRef.current) {
      headerCheckRef.current.indeterminate = paginatedAssigned.length > 0 && selectedIds.size > 0 && selectedIds.size < paginatedAssigned.length;
    }
  }, [paginatedAssigned.length, selectedIds.size]);

  const runAssignedApplicationsExport = useCallback(
    (fmt: ListExportFmt, scope: 'selection' | 'all') => {
      if (fmt !== 'csv') {
        listExportNotImplemented('Activity (assigned)', fmt, scope);
        return;
      }
      let rows = sortedAssigned;
      if (scope === 'selection') {
        if (selectedIds.size === 0) {
          showToast({ title: 'Select rows to export, or use CSV — all under Operations.', kind: 'error' });
          return;
        }
        rows = sortedAssigned.filter((a) => selectedIds.has(a.id));
      }
      if (rows.length === 0) {
        showToast({ title: 'Nothing to export for the current filters.', kind: 'error' });
        return;
      }
      const csv = applicationService.exportCsv(rows);
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `assigned-applications-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
    [sortedAssigned, selectedIds],
  );

  const peerDenied = Boolean(actorIdParam) && !canSeeActorFeed;
  const viewingLabel =
    actorIdParam && canSeeActorFeed && actorDisplayName
      ? actorDisplayName
      : actorIdParam && canSeeActorFeed
        ? 'Team member'
        : null;

  /** §25.C — today's assigned items for the right rail */
  const todayStart = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }, []);

  const todayItems = useMemo(
    () =>
      sortedAssigned.filter((a) => {
        const t = new Date(a.status_updated_at ?? a.created_at).getTime();
        return t >= todayStart;
      }),
    [sortedAssigned, todayStart],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {toast}
      <PageHeader
        icon={Activity}
        breadcrumb={[
          { label: 'Activity', href: '/activities' },
          { label: viewingLabel ? `${viewingLabel}` : 'My activities' },
        ]}
        title={viewingLabel ? `${viewingLabel} — Activity` : 'My Activities'}
        subtitle={
          peerDenied
            ? 'You do not have access to this assignee’s activity (RBAC).'
            : tab === 'assigned'
              ? actorIdParam && canSeeActorFeed
                ? `Applications assigned to ${viewingLabel ?? 'this user'}`
                : 'Applications assigned to you'
              : actorIdParam && canSeeActorFeed
                ? `Activity performed by ${viewingLabel ?? 'this user'}`
                : 'Recent activity across your org'
        }
        breadcrumbRowEnd={
          tab === 'assigned' && !peerDenied ? (
            <ListOperationsDropdown
              objectHistoryHref={objectHistoryUrl('activities_assigned')}
              onExport={(fmt, scope) => runAssignedApplicationsExport(fmt, scope)}
            />
          ) : undefined
        }
      />

      {peerDenied && (
        <div className="mb-4 rounded-lg border border-amber-700/80 bg-amber-950/35 px-4 py-3 text-sm text-amber-100">
          <span>
            Pipeline list links to teammate activity only for <strong className="text-amber-50">senior recruiters and above</strong>{' '}
            or <strong className="text-amber-50">admins</strong> (individual performance). You can always open{' '}
            <strong className="text-amber-50">your own</strong> feed.
          </span>
          <Link to="/activities" className="ml-2 text-brand-400 font-medium hover:underline">
            Back to my activity
          </Link>
        </div>
      )}

      {actorIdParam && canSeeActorFeed && (
        <div className="mb-3 flex flex-wrap items-center gap-2 text-xs vscode-text-secondary">
          <span>
            Filtered by assignee from pipeline · 9.6 RBAC: self always; peers need senior+ or admin performance access.
          </span>
          <Link to="/activities" className="text-brand-400 hover:underline font-medium">
            Clear assignee filter
          </Link>
        </div>
      )}

      <div className="flex gap-1 mb-4 rounded-xl border p-1 w-fit vscode-border vscode-bg-secondary">
        <button
          type="button"
          onClick={() => patchParams({ tab: undefined })}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            tab === 'assigned'
              ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
              : 'vscode-text-secondary hover:vscode-text-primary'
          }`}
        >
          <ClipboardList className="w-3.5 h-3.5" /> Assigned to me
        </button>
        <button
          type="button"
          onClick={() => patchParams({ tab: 'feed' })}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            tab === 'feed'
              ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
              : 'vscode-text-secondary hover:vscode-text-primary'
          }`}
        >
          <Activity className="w-3.5 h-3.5" /> Org activity
        </button>
      </div>


      {tab === 'assigned' && (
      <div className="flex gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 vscode-text-tertiary" />
          <input
            className="input pl-9"
            placeholder="Search candidate, role, client..."
            value={searchInput}
            onChange={(e) => queueSearchParam(e.target.value)}
          />
        </div>
        <select
          className="input w-auto"
          value={statusFilter}
          onChange={(e) =>
            patchParams({ status: e.target.value || undefined })
          }
        >
          <option value="">All statuses</option>
          {Object.entries(STATUS_LABEL).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
        <select
          className="input w-auto"
          value={clientFilter}
          onChange={(e) => patchParams({ client: e.target.value || undefined })}
        >
          <option value="">All clients</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <select
          className="input w-auto"
          value={dateFilter}
          onChange={(e) =>
            patchParams({
              range: e.target.value === 'all' ? undefined : e.target.value,
            })
          }
        >
          <option value="all">All dates</option>
          <option value="today">Today</option>
          <option value="week">Last 7 days</option>
          <option value="month">Last 30 days</option>
        </select>
      </div>
      )}

      {tab === 'feed' && (
        <div className="flex gap-2 mb-4 flex-wrap">
          <select
            className="input w-auto"
            value={feedEntityFilter}
            onChange={(e) => patchParams({ entity: e.target.value || undefined })}
          >
            <option value="">All entities</option>
            <option value="application">Applications</option>
            <option value="candidate">Candidates</option>
            <option value="job">Jobs</option>
            <option value="client">Clients</option>
            <option value="requisition">Requisitions</option>
            <option value="email">Emails</option>
          </select>
          <select
            className="input w-auto"
            value={feedActionFilter}
            onChange={(e) => patchParams({ action: e.target.value || undefined })}
          >
            <option value="">All actions</option>
            <option value="created">Created</option>
            <option value="updated">Updated</option>
            <option value="status_changed">Status changed</option>
            <option value="assigned">Assigned</option>
            <option value="email_sent">Email sent</option>
            <option value="cta_responded">Client responded</option>
          </select>
          <select
            className="input w-auto"
            value={dateFilter}
            onChange={(e) =>
              patchParams({
                range: e.target.value === 'all' ? undefined : e.target.value,
              })
            }
          >
            <option value="all">All dates</option>
            <option value="today">Today</option>
            <option value="week">Last 7 days</option>
            <option value="month">Last 30 days</option>
          </select>
        </div>
      )}

      {loading ? (
        <FullPageSpinner />
      ) : tab === 'feed' ? (
        peerDenied ? (
          <EmptyState
            icon={Activity}
            title="Access restricted"
            description="You cannot load this teammate’s activity feed with your current role."
          />
        ) : activities.length === 0 ? (
          <EmptyState
            icon={Activity}
            title="No activity yet"
            description={
              actorIdParam
                ? 'No logged actions for this user in the selected range.'
                : 'User actions (status changes, emails sent, assignments) will appear here.'
            }
          />
        ) : (
          <div className="card overflow-hidden p-0">
            <div className="overflow-x-auto min-w-0">
              <table className="ggj-data-table min-w-[760px]">
                <thead>
                  <tr className="border-b vscode-border">
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide vscode-text-tertiary whitespace-nowrap">
                      When
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide vscode-text-tertiary">Actor</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide vscode-text-tertiary">Entity</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide vscode-text-tertiary">Action</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide vscode-text-tertiary min-w-[200px]">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedFeed.map((act) => (
                    <tr key={act.id} className="border-b border-[color-mix(in_srgb,var(--vscode-border-primary)_55%,transparent)] last:border-0">
                      <td className="px-4 py-3 text-sm vscode-text-secondary tabular-nums whitespace-nowrap align-top">
                        {formatDateTimeGmt(act.created_at)}
                      </td>
                      <td className="px-4 py-3 text-sm vscode-text-primary align-top">
                        {(act.actor as { display_name?: string | null } | null)?.display_name ?? 'System'}
                      </td>
                      <td className="px-4 py-3 text-sm vscode-text-primary capitalize align-top">
                        {act.entity.replace(/_/g, ' ')}
                      </td>
                      <td className="px-4 py-3 text-sm vscode-text-secondary align-top">
                        {ACTION_LABEL[act.action] ?? act.action.replace(/_/g, ' ')}
                      </td>
                      <td className="px-4 py-3 text-sm min-w-0 align-top">
                        <FeedPayloadLinks payload={act.payload} />
                        {(act.payload as { to_status?: string })?.to_status && (
                          <span className="vscode-text-tertiary ml-1">
                            → {(act.payload as { to_status: string }).to_status}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <TablePagination
              page={page}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          </div>
        )
      ) : peerDenied ? (
        <EmptyState
          icon={Filter}
          title="Access restricted"
          description="You cannot list this teammate’s assigned applications with your current role."
        />
      ) : sortedAssigned.length === 0 ? (
        <EmptyState
          icon={Filter}
          title="No activities"
          description={
            actorIdParam
              ? 'No applications assigned to this user match your filters.'
              : 'Applications assigned to you will appear here. Assign applications from the Application detail page.'
          }
        />
      ) : (
        <div className="card overflow-hidden p-0">
          <DataTableToolbar
            hasTable={true}
            newLabel="New application"
            newHref="/applications/new"
            selectedIds={Array.from(selectedIds)}
            onEdit={(id) => navigate(`/applications/${id}`)}
            onExport={() => runAssignedApplicationsExport('csv', 'all')}
            onImport={() =>
                showToast({ title: 'Import is not available on this list. Add applications from jobs or candidates.', kind: 'error' })
            }
            onManageRights={() => setManageRightsOpen(true)}
            toolbarEnd={
              <ListConfigureButton onClick={() => setConfigureOpen(true)} title="Assigned applications table" />
            }
          />
          <div className="overflow-x-auto min-w-0">
          <table className="ggj-data-table min-w-[800px]">
            <thead>
              <tr className="border-b vscode-border">
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    ref={headerCheckRef}
                    checked={paginatedAssigned.length > 0 && selectedIds.size === paginatedAssigned.length}
                    onChange={() => {
                      if (selectedIds.size === paginatedAssigned.length) setSelectedIds(new Set());
                      else setSelectedIds(new Set(paginatedAssigned.map((a) => a.id)));
                    }}
                    className="rounded border-gray-600"
                  />
                </th>
                <SortableTh
                  label="Candidate"
                  column="candidate"
                  current={actSortBy}
                  asc={actSortAsc}
                  onSort={handleActSort}
                  className={colClass(show('candidate'), 'text-left')}
                />
                <SortableTh
                  label="Job"
                  column="job"
                  current={actSortBy}
                  asc={actSortAsc}
                  onSort={handleActSort}
                  className={colClass(show('job'), 'text-left')}
                />
                <SortableTh
                  label="Client"
                  column="client"
                  current={actSortBy}
                  asc={actSortAsc}
                  onSort={handleActSort}
                  className={colClass(show('client'), 'text-left')}
                />
                <SortableTh
                  label="Status"
                  column="status"
                  current={actSortBy}
                  asc={actSortAsc}
                  onSort={handleActSort}
                  className={colClass(show('status'), 'text-left')}
                />
                <SortableTh
                  label="Created When"
                  column="created_when"
                  current={actSortBy}
                  asc={actSortAsc}
                  onSort={handleActSort}
                  className={colClass(show('created_when'), 'text-left whitespace-nowrap')}
                />
                <SortableTh
                  label="Updated"
                  column="updated"
                  current={actSortBy}
                  asc={actSortAsc}
                  onSort={handleActSort}
                  className={colClass(show('updated'), 'text-left')}
                />
                <th className="px-4 py-3 w-10" aria-hidden />
              </tr>
            </thead>
            <tbody>
              {paginatedAssigned.map((app) => (
                <tr
                  key={app.id}
                  className={`group border-b vscode-border last:border-0 ${
                    selectedIds.has(app.id) ? 'bg-emerald-500/8' : 'hover:vscode-bg-hover'
                  }`}
                >
                  <td className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(app.id)}
                      onChange={() => {
                        const next = new Set(selectedIds);
                        if (next.has(app.id)) next.delete(app.id);
                        else next.add(app.id);
                        setSelectedIds(next);
                      }}
                      className="rounded border-gray-600"
                    />
                  </td>
                  <td className={colClass(show('candidate'), 'px-4 py-3')}>
                    <Link
                      to={`/applications/${app.id}`}
                      className="flex items-center gap-2 font-medium vscode-text-primary hover:text-emerald-500"
                    >
                      <TableAvatar name={app.candidate?.full_name ?? '?'} size={22} />
                      {app.candidate?.full_name ?? '—'}
                    </Link>
                  </td>
                  <td className={colClass(show('job'), 'px-4 py-3 text-sm vscode-text-secondary')}>{app.job?.title ?? '—'}</td>
                  <td className={colClass(show('client'), 'px-4 py-3 text-sm vscode-text-secondary')}>{app.client?.name ?? '—'}</td>
                  <td className={colClass(show('status'), 'px-4 py-3')}>
                    <StatusBadge status={app.status} />
                  </td>
                  <td className={colClass(show('created_when'), 'px-4 py-3 text-sm vscode-text-secondary tabular-nums whitespace-nowrap')}>
                    {formatCreatedWhen(app.created_at)}
                  </td>
                  <td className={colClass(show('updated'), 'px-4 py-3 text-sm vscode-text-tertiary')}>{formatTimeAgo(app.status_updated_at)}</td>
                  {/* §25.C — hover row reveals ⋯ → Edit / Complete / Delete */}
                  <td className="w-10 px-2 py-3 text-right">
                    <ActivityRowMenu appId={app.id} onNavigate={navigate} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <TablePagination
            page={page}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          />
        </div>
      )}
      <ConfigureListColumnsModal
        open={configureOpen}
        onClose={() => setConfigureOpen(false)}
        entityLabel="Assigned applications"
        columns={ACTIVITIES_ASSIGNED_COLUMNS}
        value={visible}
        onChange={setColumn}
        onReset={reset}
      />
      <GenericManageRightsModal
        open={manageRightsOpen}
        onClose={() => setManageRightsOpen(false)}
        orgId={user?.orgId ?? null}
        entityKey="activities_assigned"
        subtitle="Per-user access for the My Activities — Assigned tab — stored locally per browser."
      />

      {/* §25.C — persistent right rail: today's pinned items */}
      <TodaysPinnedRail items={todayItems} />
    </div>
  );
}

// ── §25.C helpers ────────────────────────────────────────────────────────────

interface ActivityRowMenuProps {
  appId: string;
  onNavigate: (path: string) => void;
}

function ActivityRowMenu({ appId, onNavigate }: ActivityRowMenuProps) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-flex">
      <button
        type="button"
        title="Actions"
        aria-label="Row actions"
        className="ggj-row-actions flex h-6 w-6 items-center justify-center rounded vscode-text-tertiary opacity-0 transition-opacity group-hover:opacity-100 hover:vscode-bg-hover hover:vscode-text-primary"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <MoreHorizontal className="h-3.5 w-3.5" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} aria-hidden />
          <div className="absolute right-0 top-7 z-40 min-w-[128px] rounded-[8px] border py-1 shadow-lg vscode-border vscode-bg-elevated">
            <button
              type="button"
              className="flex w-full items-center gap-2 px-3 py-1.5 text-[13px] vscode-text-secondary hover:vscode-bg-hover hover:vscode-text-primary"
              onClick={(e) => { e.stopPropagation(); setOpen(false); onNavigate(`/applications/${appId}`); }}
            >
              Edit
            </button>
            <button
              type="button"
              className="flex w-full items-center gap-2 px-3 py-1.5 text-[13px] text-emerald-600 hover:vscode-bg-hover dark:text-emerald-400"
              onClick={(e) => { e.stopPropagation(); setOpen(false); }}
            >
              Complete
            </button>
            <button
              type="button"
              className="flex w-full items-center gap-2 px-3 py-1.5 text-[13px] text-red-600 hover:vscode-bg-hover dark:text-red-400"
              onClick={(e) => { e.stopPropagation(); setOpen(false); }}
            >
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  );
}

type PinnedApp = Pick<Application, 'id' | 'status' | 'created_at'> & {
  candidate?: { full_name?: string | null } | null;
  job?: { title?: string | null } | null;
  status_updated_at: string;
};

const PINNED_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  interview_1_scheduled: CalendarCheck,
  interview_2_scheduled: CalendarCheck,
  submitted: CheckSquare,
  shortlisted: CheckSquare,
};

function TodaysPinnedRail({ items }: { items: PinnedApp[] }) {
  const [open, setOpen] = useState(true);
  if (!open) {
    return (
      <button
        type="button"
        className="fixed bottom-6 right-6 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500 shadow-lg text-white hover:bg-emerald-600 transition-colors"
        title="Show today's items"
        onClick={() => setOpen(true)}
      >
        <CalendarCheck className="h-5 w-5" />
      </button>
    );
  }

  return (
    <aside
      aria-label="Today's pinned items"
      className="fixed bottom-0 right-0 top-[var(--topbar-height,56px)] z-10 flex w-[280px] flex-col border-l vscode-border vscode-bg-secondary shadow-lg"
    >
      <div className="flex items-center justify-between border-b px-3 py-2.5 vscode-border">
        <p className="text-[13px] font-semibold vscode-text-primary">Today's items</p>
        <button
          type="button"
          className="rounded p-0.5 vscode-text-tertiary transition-colors hover:vscode-text-primary hover:vscode-bg-hover"
          aria-label="Close today's items"
          onClick={() => setOpen(false)}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-3">
        {items.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-8 text-center">
            <CalendarCheck className="h-8 w-8 text-emerald-500/40" />
            <p className="text-[13px] vscode-text-tertiary">No activity today yet</p>
          </div>
        ) : (
          <ul className="space-y-2">
            {items.slice(0, 20).map((app) => {
              const IconComp = PINNED_ICON[app.status] ?? CheckSquare;
              const isCall =
                app.status === 'interview_1_scheduled' || app.status === 'interview_2_scheduled';
              return (
                <li key={app.id} className="rounded-[8px] border p-2.5 vscode-border vscode-bg-primary">
                  <div className="flex items-start gap-2">
                    <span
                      className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${
                        isCall ? 'bg-blue-500/15 text-blue-500' : 'bg-emerald-500/15 text-emerald-600'
                      }`}
                    >
                      {isCall ? (
                        <Phone className="h-3.5 w-3.5" />
                      ) : (
                        <IconComp className="h-3.5 w-3.5" />
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13px] font-medium vscode-text-primary">
                        {app.candidate?.full_name ?? 'Unknown'}
                      </p>
                      <p className="truncate text-[11px] vscode-text-tertiary">
                        {app.job?.title ?? '—'}
                      </p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {items.length > 0 && (
        <div className="border-t px-3 py-2 vscode-border">
          <p className="text-[11px] vscode-text-tertiary">
            {items.length} item{items.length !== 1 ? 's' : ''} updated today
          </p>
        </div>
      )}
    </aside>
  );
}
